import Display from '@loggi/ui/components/display';
import LoaderMask from '@loggi/ui/components/loader-mask';
import Text from '@loggi/ui/components/text';
import colors from '@loggi/ui/components/utils/colors';
import { privatePackageTrackerModel } from 'app/app.models/private-package-tracker.model';
import getStatusPath from 'app/app.utils';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  AFTER_CONFIRMING_ADDRESS_CHANGE,
  CATEGORY_NAME
} from 'shared/analytics/analytics.constants';
import sendGoogleAnalyticsDataEvent from 'shared/analytics/analytics.utils';
import BicolorTemplate from 'shared/bicolor-template/bicolor-template.component';
import FacilitateDelivery from 'shared/facilitate-delivery/facilitate-delivery.component';
import MainView from 'shared/main-view/main-view.component';
import WarningScreen from 'shared/warning-screen/warning-screen.component';
import * as appActions from 'tracker.actions';
import { CAN_EDIT_FULL_ADDRESS } from 'tracker.constants';
import {
  ADDITIONAL_RECEIVERS,
  CLASSIFICATION,
  INSTRUCTIONS
} from 'tracker.routes';
import SuccessHeader from './success-header/success-header.component';
import {
  WARNING_BUTTON_VALUE,
  WARNING_PROBLEM_DESCRIPTION,
  WARNING_TITLE
} from './success.constants';
import getUpdatedPackageInfo from './success.service';
import styles from './success.styles.scss';

export class SuccessContainer extends Component {
  constructor(props) {
    super(props);

    const {
      match: {
        params: { packageId }
      },
      history
    } = props;

    this.queryString = history.location.search;

    if (props.privatePackageTracker) {
      const { permissions } = props.privatePackageTracker || null;
      this.canEditAddress =
        permissions && permissions.includes(CAN_EDIT_FULL_ADDRESS);
    }

    this.state = {
      isLoading: true,
      getUpdatedPackageInfoFailed: false,
      shouldRedirect: !props.privatePackageTracker,
      statusPath: getStatusPath(packageId)
    };
  }

  componentDidMount() {
    const { shouldRedirect } = this.state;

    if (!shouldRedirect) {
      const { actions } = this.props;

      actions.saveScreenFlow({
        hasFinishedScreenFlow: true
      });

      this._getUpdatedPackageInfo();
    }
  }

  _getUpdatedPackageInfo = () => {
    const {
      match: {
        params: { packageId }
      }
    } = this.props;

    const onSuccess = this._onSuccessQuery;
    const onFailure = this._onFailureQuery;

    getUpdatedPackageInfo(packageId, onSuccess, onFailure);
  };

  _onSuccessQuery = response => {
    const { actions, privatePackageTracker } = this.props;

    const updatedPrivatePackageTracker = {
      ...privatePackageTracker,
      destinationInfo: response.destinationInfo,
      permissions: response.permissions
    };

    actions.savePrivatePackageTracker({
      privatePackageTracker: updatedPrivatePackageTracker
    });

    return this.setState({
      isLoading: false,
      getUpdatedPackageInfoFailed: false
    });
  };

  _onFailureQuery = () => {
    return this.setState({
      isLoading: false,
      getUpdatedPackageInfoFailed: true
    });
  };

  _renderWarningScreen = () => {
    const warningButtonsProps = [
      {
        value: WARNING_BUTTON_VALUE,
        onClick: this._getUpdatedPackageInfo
      }
    ];

    return (
      <WarningScreen title={WARNING_TITLE} buttonsProps={warningButtonsProps}>
        <Text color={colors.type.DARK}>{WARNING_PROBLEM_DESCRIPTION}</Text>
      </WarningScreen>
    );
  };

  _sendGoogleAnalyticsEvent = prop => {
    const event = {
      categoryName: CATEGORY_NAME.EDIT_ADDRESS,
      action: AFTER_CONFIRMING_ADDRESS_CHANGE[prop].ACTION,
      label: AFTER_CONFIRMING_ADDRESS_CHANGE[prop].LABEL
    };
    sendGoogleAnalyticsDataEvent(event);
  };

  _goToNextScreen = path => {
    const { privatePackageTracker, actions, history } = this.props;

    actions.savePrivatePackageTracker({
      privatePackageTracker
    });

    if (this.queryString) {
      return history.push(`${path}${this.queryString}`);
    }

    return history.push(path);
  };

  _onClickGoToTracker = () => {
    this._sendGoogleAnalyticsEvent('REDIRECT_TO_PACKAGE_STATUS');
    const { history } = this.props;
    const { statusPath } = this.state;

    if (this.queryString) {
      history.replace(`${statusPath}${this.queryString}`);
      return;
    }
    history.replace(statusPath);
  };

  render() {
    const { shouldRedirect, statusPath } = this.state;

    if (shouldRedirect) {
      const { history } = this.props;

      history.replace(statusPath);

      return null;
    }

    const { privatePackageTracker } = this.props;
    const { isLoading, getUpdatedPackageInfoFailed } = this.state;

    const {
      packageInfo: { companyName },
      recipientInfo: { firstName },
      destinationInfo: { instructions, classificationType, additionalReceivers }
    } = privatePackageTracker;

    return (
      <MainView className={`tracker-content ${styles.successMainView}`}>
        <Display when={isLoading}>
          <LoaderMask />
        </Display>
        <BicolorTemplate
          contentBackground={colors.type.WHITE}
          headerClassName={styles.headerLayout}
          header={
            <SuccessHeader
              user={firstName}
              company={companyName}
              onClick={this._onClickGoToTracker}
            />
          }
        >
          {getUpdatedPackageInfoFailed && this._renderWarningScreen()}
          {!getUpdatedPackageInfoFailed && (
            <FacilitateDelivery
              destinationType={classificationType}
              onClickDestinationType={() =>
                this._goToNextScreen(CLASSIFICATION)
              }
              destinationDescription={instructions}
              onClickDestinationDescription={() =>
                this._goToNextScreen(INSTRUCTIONS)
              }
              additionalReceivers={additionalReceivers}
              onClickAdditionalReceivers={() =>
                this._goToNextScreen(ADDITIONAL_RECEIVERS)
              }
            />
          )}
        </BicolorTemplate>
      </MainView>
    );
  }
}

function mapStateToProps(state) {
  return {
    privatePackageTracker: state.data.privatePackageTracker,
    hasFinishedScreenFlow: state.screenFlow.hasFinishedScreenFlow
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(appActions, dispatch)
  };
}

SuccessContainer.displayName = 'SuccessContainer';

SuccessContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      packageId: PropTypes.string.isRequired
    })
  }).isRequired,
  actions: PropTypes.shape({
    savePrivatePackageTracker: PropTypes.func.isRequired,
    saveScreenFlow: PropTypes.func.isRequired
  }).isRequired,
  history: PropTypes.shape({
    location: PropTypes.shape({
      search: PropTypes.string
    }).isRequired,
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired
  }).isRequired,
  privatePackageTracker: PropTypes.shape(privatePackageTrackerModel).isRequired
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SuccessContainer);
